import { useState } from 'react';
import { format, eachDayOfInterval, startOfMonth, endOfMonth, isSameDay, addMonths, subMonths, getDay, isWithinInterval } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface UserCalendarProps {
    subscriptions: any[]; // Using any for simplicity as per existing UserDetailScreen
}

export function UserCalendar({ subscriptions }: UserCalendarProps) {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const startDate = startOfMonth(currentMonth);
    const endDate = endOfMonth(currentMonth);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    const startDayOfWeek = getDay(startDate);
    const placeholders = Array.from({ length: startDayOfWeek });

    const getSubscriptionForDate = (date: Date) => {
        // Find subscription where date is between start and end
        return subscriptions.find(sub => {
            const subStart = new Date(sub.start_date);
            const subEnd = new Date(sub.end_date);
            // normalization to avoid time issues
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            subStart.setHours(0, 0, 0, 0);
            subEnd.setHours(0, 0, 0, 0);

            return d >= subStart && d <= subEnd;
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <span className="font-medium text-lg">
                    {format(currentMonth, 'MMMM yyyy')}
                </span>
                <div className="flex gap-1">
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-xs text-muted-foreground font-medium py-1">
                        {day}
                    </div>
                ))}

                {placeholders.map((_, i) => (
                    <div key={`placeholder-${i}`} className="aspect-square" />
                ))}

                {days.map(day => {
                    const sub = getSubscriptionForDate(day);
                    const isBooked = !!sub;
                    const isToday = isSameDay(day, new Date());

                    return (
                        <div
                            key={day.toISOString()}
                            className={cn(
                                "aspect-square flex flex-col items-center justify-center rounded-md border text-xs relative",
                                isBooked
                                    ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-300"
                                    : "bg-background text-muted-foreground",
                                isToday && "ring-2 ring-primary ring-offset-1"
                            )}
                            title={sub ? `${sub.shift_name} - Seat ${sub.seat_number}` : "No booking"}
                        >
                            <span className="font-semibold">{format(day, 'd')}</span>
                            {isBooked && (
                                <span className="text-[10px] opacity-80 truncate w-full px-1">
                                    {sub.shift_name}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
